import { useState } from 'react';
import { Mail, Phone, MapPin, ArrowLeft, Send, Check } from 'lucide-react';
import { Logo } from '../Logo';
import { Footer } from '../Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/leads/demo-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, teamSize: 'N/A', role: 'Contact Form' }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      <nav className="sticky top-0 z-50 bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2"><Logo /></a>
          <a href="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" />Back to Home</a>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-8 py-20">
        <h1 className="text-4xl sm:text-5xl font-bold font-outfit mb-4">Get in Touch</h1>
        <p className="text-lg text-zinc-400 mb-16 max-w-xl">Have questions about D3 Command? We'd love to hear from you. Reach out and our team will respond within one business day.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            {status === 'success' ? (
              <div className="p-8 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-center" data-testid="contact-success">
                <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Message Sent</h2>
                <p className="text-zinc-400">Thank you for reaching out. We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className="block text-sm text-zinc-400 mb-1.5">Full Name</label>
                    <input required type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Doe"
                      className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50" data-testid="contact-name" /></div>
                  <div><label className="block text-sm text-zinc-400 mb-1.5">Email</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@company.com"
                      className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50" data-testid="contact-email" /></div>
                </div>
                <div><label className="block text-sm text-zinc-400 mb-1.5">Company</label>
                  <input type="text" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Company name"
                    className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50" /></div>
                <div><label className="block text-sm text-zinc-400 mb-1.5">Message</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} placeholder="How can we help?"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50 resize-none" data-testid="contact-message" /></div>
                <button type="submit" disabled={status === 'sending'}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="contact-submit">
                  <Send className="w-4 h-4" />{status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                {status === 'error' && <p className="text-sm text-red-400">Something went wrong. Please try again or email us directly.</p>}
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 space-y-5">
              <h3 className="font-semibold text-white">Contact Information</h3>
              <a href="mailto:support@d3command.com" className="flex items-center gap-3 text-sm text-zinc-400 hover:text-indigo-400 transition-colors"><Mail className="w-4 h-4 text-indigo-400 shrink-0" />support@d3command.com</a>
              <a href="tel:+18305803004" className="flex items-center gap-3 text-sm text-zinc-400 hover:text-indigo-400 transition-colors"><Phone className="w-4 h-4 text-indigo-400 shrink-0" />(830) 580-3004</a>
              <div className="flex items-start gap-3 text-sm text-zinc-400"><MapPin className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" /><span>2807 Allen St #2164<br />Dallas, TX 75204</span></div>
            </div>
            <div className="p-6 rounded-2xl bg-gradient-to-br from-indigo-500/5 to-cyan-500/5 border border-indigo-500/10">
              <h3 className="font-semibold text-white mb-2">Request a Demo</h3>
              <p className="text-sm text-zinc-400 mb-4">See D3 Command in action with a personalized walkthrough.</p>
              <a href="/#demo" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 text-sm font-medium hover:bg-indigo-500/30 transition-colors">Request Demo</a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
